// The link somebody was given, turned into the screen they expected.
// Factory-owned.
//
// THE PROBLEM. This app navigates through a MEMORY history — `main.js` explains
// why — so the URL the server served is invisible to the router. Boot and the
// native `appUrlOpen` handler both call `app.route(url)`, which emits a `route`
// event on the bus, and in a scaffolded app NOTHING LISTENS. So every link into
// the app lands on Home: the marketing page's "Book Optimus" button, the
// operator's `/app/admin`, the shared link a customer pastes to a friend, and
// every deep link the App Store lists under Universal Links.
//
// That is invisible until somebody builds a page per thing. Six model pages,
// six buttons, one destination — and the pages look like they work, because
// the app opens.
//
// THE CONTRACT. An app declares its own parameter vocabulary once, here, by
// calling `defineLinks()`. Both directions come from that one declaration:
//
//   parseLink()  a URL  → a router location   (arriving)
//   linkTo()     params → a URL               (the marketing page writing hrefs)
//
// One declaration, so a page cannot write a link the app does not read. That
// inverse is the whole reason this is a module rather than four lines in
// App.vue: a site generator needs `linkTo()`, and a site that builds its own
// URLs drifts from the app the first time a parameter is renamed.
//
// THREE RULES, each learned the hard way:
//
//   · VALIDATE AGAINST THE APP'S OWN DATA. An unknown id resolves to null and
//     the app opens Home. A link that lands on a not-found screen is worse than
//     one that lands on Home, because links get shortened, pasted and mangled,
//     and somebody arriving from an ad reads a not-found screen as a broken app.
//   · ORDER BY SPECIFICITY. An item beats a category beats a bare mode. The
//     first matching rule wins, so declare them in that order.
//   · PARSE THE PATHNAME, NOT ONLY THE QUERY. The operator link people actually
//     paste is `/app/admin`, and a query-only parser boots it to Home.

import { bus } from './state.js';

/** @type {{match: (loc: object) => (object|null), href?: (params: object) => (string|null)}[]} */
let rules = [];
let router = null;

/**
 * Declare this app's link vocabulary.
 *
 * Call once, from `App.vue` or a module it imports, BEFORE the app finishes
 * booting — `start()` replays the URL the app was opened with, so a declaration
 * that arrives late still works.
 *
 * ```js
 * defineLinks([
 *   // Most specific first.
 *   { name: 'item',  match: ({ params }) => (byId(params.get('item')) ? { name: 'item', params: { id: params.get('item') } } : null),
 *                    href: ({ item }) => (item ? `/app?item=${encodeURIComponent(item)}` : null) },
 *   { name: 'admin', match: ({ pathname }) => (/\/admin\/?$/.test(pathname) ? { name: 'admin' } : null),
 *                    href: () => '/app/admin' },
 * ]);
 * ```
 *
 * @param {object[]} definitions
 */
export function defineLinks(definitions) {
  rules = Array.isArray(definitions) ? definitions : [];
}

/**
 * A URL this app was opened with → a router location, or null.
 *
 * Null means "nothing recognised", and the caller opens Home. That is the only
 * acceptable failure: a link is user input.
 *
 * @param {string|URL} url
 */
export function parseLink(url) {
  let loc;
  try {
    loc = new URL(String(url), 'http://localhost');
  } catch {
    return null;
  }
  const input = { pathname: loc.pathname, search: loc.search, params: loc.searchParams };

  for (const rule of rules) {
    let hit = null;
    // A rule that throws is a bug in one rule, not a reason to drop the link.
    try { hit = rule.match?.(input) ?? null; } catch { hit = null; }
    if (hit) return hit;
  }
  return null;
}

/**
 * The inverse: parameters → a URL the marketing site can put in an href.
 *
 * Returns null when no rule can express it, which a generator should treat as a
 * build failure rather than writing a bare `/app` — a button that silently
 * loses what it was about is the defect this module exists for.
 *
 * @param {string} name  the rule's `name`
 * @param {object} params
 */
export function linkTo(name, params = {}) {
  const rule = rules.find((r) => r.name === name);
  if (!rule?.href) return null;
  try { return rule.href(params) ?? null; } catch { return null; }
}

/**
 * Wire the bus to the router. Factory-owned; `main.js` calls it.
 *
 * Listens rather than reading `location` itself, because the same handling has
 * to serve a cold start AND the native `appUrlOpen` event, and those arrive by
 * different routes at different times.
 */
export function start(vueRouter) {
  router = vueRouter;
  bus.on('route', (url) => {
    const to = parseLink(url);
    if (!to) return;                       // unrecognised: leave the app on Home
    try { router.push(to); } catch { /* a bad location must not break boot */ }
  });
}
